import { connect, tokenAuthenticator, type NatsConnection, StringCodec } from 'nats.ws'
import type {
  CommandRoute,
  QueryRoute,
  EventRoute,
  MessageWithTypeVersion,
  InputOf,
  ReplyOf,
  PayloadOf,
  MessageFields,
} from '@omniflow/contracts-core'
import { isSystemErrorReply, BusSystemError, formatBusErrorDetails } from '@omniflow/contracts-core'
import { buildCommand, buildQuery } from './envelope'

const sc = StringCodec()

export type AnyRequestRoute =
  | CommandRoute<string, MessageWithTypeVersion, unknown>
  | QueryRoute<string, MessageWithTypeVersion, unknown>
export type AnyEventRoute = EventRoute<string, MessageWithTypeVersion>

export interface NatsBusRequestOpts {
  timeoutMs?: number
  traceId?: string
  /** Адресный transport subject при неизменных input/reply schemas route. */
  subject?: string
}

export interface NatsBusOptions {
  servers: string
  /**
   * JWT или функция, возвращающая актуальный JWT: nats.ws зовёт её при каждом
   * (пере)подключении, поэтому продлённый токен подхватывается без пересоздания шины.
   */
  token: string | (() => string)
  onDisconnect?: () => void
  onReconnect?: () => void
  onError?: (err: Error) => void
  /**
   * Соединение закрыто терминально (auto-reconnect прекращён). nats.ws по
   * умолчанию (ignoreAuthErrorAbort=false) закрывает соединение после 2 подряд
   * auth-ошибок — `err` тогда несёт AUTHORIZATION_VIOLATION. `err === undefined`
   * при штатном close() (logout / reconnect нового bus).
   */
  onClose?: (err?: Error) => void
}

/** Окно вокруг `exp` токена соединения, в котором разрыв считается сменой ключа сессии. */
export const SESSION_KEY_WINDOW_SEC = 30

/**
 * Почему оборвалось соединение: сервер рвёт сокет в момент `exp` токена, с которым
 * оно было открыто (смена ключа сессии), всё остальное — сеть.
 */
export const classifyDisconnect = (
  nowMs: number,
  connExpSec: number | null,
): 'network' | 'session-key' =>
  connExpSec !== null && Math.abs(nowMs - connExpSec * 1000) <= SESSION_KEY_WINDOW_SEC * 1000
    ? 'session-key'
    : 'network'

/** Код NatsError (если есть) — без `any`, через сужение unknown. */
export const natsErrorCode = (err: unknown): string | undefined => {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: unknown }).code
    return typeof code === 'string' ? code : undefined
  }
  return undefined
}

/**
 * Отличить auth-провал (истёкший/невалидный JWT, отказ auth callout) от
 * сетевого обрыва. Auth — это AUTHORIZATION_VIOLATION / AUTHENTICATION_EXPIRED
 * по коду NatsError, с fallback по тексту сообщения.
 */
export const isAuthError = (err: unknown): boolean => {
  const code = natsErrorCode(err)
  if (code === 'AUTHORIZATION_VIOLATION' || code === 'AUTHENTICATION_EXPIRED') return true
  const msg = err instanceof Error ? err.message : ''
  return /authorization violation|authentication expired/i.test(msg)
}

/**
 * Привести произвольное значение к `Error`. Нужно на границе с nats.ws: в
 * `status().data` и в реджекте итератора подписки лежит не `Error` (для
 * permission violation — строка), поэтому прямой `as Error` давал в логах
 * `err.message === undefined` («NATS error:  undefined»).
 */
const toError = (value: unknown, fallback: string): Error => {
  if (value instanceof Error) return value
  if (typeof value === 'string' && value.length > 0) return new Error(value)
  if (typeof value === 'object' && value !== null) {
    const message = (value as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) return new Error(message)
    return new Error(`${fallback}: ${JSON.stringify(value)}`)
  }
  return new Error(fallback)
}

export interface NatsBus {
  nc: NatsConnection
  /**
   * Отправить command/query и дождаться typed reply.
   *
   * Два overload'а:
   *  - **Route-based** (typed): `bus.request(SbcRoutes.sipusersList, dto)` — bus
   *    собирает envelope (type/version/commandId|queryId/tenantId/timestamp) сам,
   *    return type выводится из route'а через `ReplyOf<R>`.
   *  - **@deprecated string-based**: `bus.request(subject, payload, timeoutMs?)` —
   *    старый API, payload собирает caller через `buildCommand`/`buildQuery`.
   */
  request: {
    <R extends AnyRequestRoute>(
      route: R,
      dto: Omit<InputOf<R>, MessageFields>,
      opts?: NatsBusRequestOpts,
    ): Promise<ReplyOf<R>>
    /** @deprecated use `request(Route, dto)` */
    <T>(subject: string, payload: unknown, timeoutMs?: number): Promise<T>
  }
  subscribe: {
    <R extends AnyEventRoute>(
      route: R,
      callback: (payload: PayloadOf<R>) => void,
    ): ReturnType<NatsConnection['subscribe']>
    /** @deprecated use `subscribe(Route, cb)` или передавай subject из `Routes.X.subject` если нужен glob */
    (subject: string, callback: (data: unknown) => void): ReturnType<NatsConnection['subscribe']>
  }
  close: () => Promise<void>
}

export const createNatsBus = async (opts: NatsBusOptions): Promise<NatsBus> => {
  const nc = await connect({
    servers: opts.servers,
    authenticator: tokenAuthenticator(opts.token),
    reconnect: true,
    maxReconnectAttempts: -1,
  })

  ;(async () => {
    for await (const s of nc.status()) {
      if (s.type === 'disconnect') opts.onDisconnect?.()
      if (s.type === 'reconnect') opts.onReconnect?.()
      if (s.type === 'error') opts.onError?.(toError(s.data, `NATS status error: ${s.type}`))
    }
  })()

  // Терминальный сигнал: nats.ws закрывает соединение после auth-abort (или
  // штатного close()). Без этой подписки store не узнаёт, что reconnect мёртв.
  void nc.closed().then((err) => opts.onClose?.(err as Error | undefined))

  const rawRequest = async <T>(subject: string, payload: unknown, timeoutMs = 10_000): Promise<T> => {
    const msg = await nc.request(subject, sc.encode(JSON.stringify(payload)), { timeout: timeoutMs })
    const parsed = JSON.parse(sc.decode(msg.data)) as { isOk?: boolean; error?: string; details?: unknown }

    if (parsed.isOk === false) {
      // details теперь может быть объектом (ValidationErrorDetails) — форматируем,
      // иначе попадёт "[object Object]" в текст ошибки.
      const errMsg = [parsed.error ?? 'NATS request failed', formatBusErrorDetails(parsed.details)]
        .filter(Boolean)
        .join(': ')
      throw new Error(errMsg)
    }

    return parsed as T
  }

  function request<R extends AnyRequestRoute>(
    route: R,
    dto: Omit<InputOf<R>, MessageFields>,
    opts?: NatsBusRequestOpts,
  ): Promise<ReplyOf<R>>
  function request<T>(subject: string, payload: unknown, timeoutMs?: number): Promise<T>
  function request(
    routeOrSubject: AnyRequestRoute | string,
    dtoOrPayload: unknown,
    optsOrTimeout?: NatsBusRequestOpts | number,
  ): Promise<unknown> {
    if (typeof routeOrSubject === 'string') {
      // Legacy string-based overload: rawRequest throw'ит на ЛЮБОМ {isOk:false}
      // (включая business). Используется только для миграционных edge-cases —
      // новые callsite'ы используют Route-вариант ниже.
      return rawRequest(routeOrSubject, dtoOrPayload, optsOrTimeout as number | undefined)
    }
    // Route-based overload: возвращает business reply как union (success |
    // business-failure), на system-errors throw'ает BusSystemError. Это
    // позволяет каллер'у писать только узкий business switch без повторения
    // case'ов handler_error/validation_error/invalid_json в каждом месте.
    return routeRequest(routeOrSubject, dtoOrPayload as Record<string, unknown>, (optsOrTimeout as NatsBusRequestOpts | undefined) ?? {})
  }

  const routeRequest = async (
    route: AnyRequestRoute,
    dto: Record<string, unknown>,
    reqOpts: NatsBusRequestOpts,
  ): Promise<unknown> => {
    const envelope = route.kind === 'command'
      ? buildCommand(route.type as string, dto)
      : buildQuery(route.type as string, dto)
    if (reqOpts.traceId) {
      (envelope as Record<string, unknown>).traceId = reqOpts.traceId
    }
    // Subject чистый: identity актора добавляет NATS-сервер (Nats-Request-Info)
    // на границе import PUBLIC→SERVER (эпик DUOACC) — не клиент.
    const targetSubject = reqOpts.subject ?? route.subject
    const msg = await nc.request(targetSubject, sc.encode(JSON.stringify(envelope)), {
      timeout: reqOpts.timeoutMs ?? 10_000,
    })
    const decoded = JSON.parse(sc.decode(msg.data)) as unknown
    // System errors из bus.respond (см. BusSystemError JSDoc в contracts-core).
    if (isSystemErrorReply(decoded)) {
      throw new BusSystemError(decoded.error, decoded.details, targetSubject)
    }
    return decoded
  }

  const rawSubscribe = (subject: string, callback: (data: unknown) => void) => {
    const sub = nc.subscribe(subject)
    void (async () => {
      try {
        for await (const msg of sub) {
          callback(JSON.parse(sc.decode(msg.data)))
        }
      } catch (err) {
        // Итератор подписки реджектится, когда сервер её отбивает (нет прав на
        // subject у роли) или рвётся соединение. Раньше промис был floating —
        // каждая такая подписка всплывала в `unhandledrejection` и рисовала
        // пользователю красный тост (страница на 10 подписок = 10 тостов).
        // Ошибку НЕ проглатываем: отдаём в onError-канал шины — стор кладёт её
        // в lastError и пишет в консоль с указанием subject'а.
        opts.onError?.(toError(err, `NATS subscription failed: ${subject}`))
      }
    })()
    return sub
  }

  function subscribe<R extends AnyEventRoute>(
    route: R,
    callback: (payload: PayloadOf<R>) => void,
  ): ReturnType<NatsConnection['subscribe']>
  function subscribe(subject: string, callback: (data: unknown) => void): ReturnType<NatsConnection['subscribe']>
  function subscribe(
    routeOrSubject: AnyEventRoute | string,
    // any (не unknown) — иначе TS2394: первый overload объявляет
    // `callback: (payload: PayloadOf<R>) => void` через generic R, и
    // implementation `(data: unknown) => void` не покрывает его (TS не делает
    // bivariance check с generic-overload'ами для callback-параметров).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (data: any) => void,
  ): ReturnType<NatsConnection['subscribe']> {
    const subject = typeof routeOrSubject === 'string' ? routeOrSubject : routeOrSubject.subject
    return rawSubscribe(subject, callback)
  }

  return { nc, request, subscribe, close: () => nc.close() }
}
